<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import type {
  TableField,
  TableAction,
  SortConfig,
  FilterField,
  Params,
} from "./table.ts";
import DataTableHeader from "./DataTableHeader.vue";
import DataTableDesktop from "./DataTableDesktop.vue";
import DataTableMobile from "./DataTableMobile.vue";
import DataTablePagination from "./DataTablePagination.vue";
import FilterDialog from "./FilterDialog.vue";
import { api } from "../../api/Api";
import { Permission } from "../../types/auth";
import { documentActions, isEmpty } from "../../utils/CommonFunctions";
import MessageBox from "../../services/MessageBox";
import { tokenManager } from "../../api/utils/tokenManager.ts";

const props = withDefaults(
  defineProps<{
    fields: TableField[];
    actions?: TableAction[];
    filterFields?: FilterField[];
    rows?: any;
    customUrl?: string;
    endPoint?: string;
    allowDisable?: boolean;
    allowEdit?: boolean;
    showQuickView?: boolean;
    refreshData?: boolean;
    permissions?: Permission[];
    title: string | null;
    showHeader?: boolean;
    showPagination?: boolean;
    loadData?: boolean;
    remote?: boolean;
    showActions?: boolean;
    uid?: string | number;
    showShareActions?: boolean;
    titleField?: string;
    showChildRow?: boolean;
    childRowFields?: TableField[];
    childRowTitle?: string;
    childRowTableWidth?: string;
    childRowDataField?: string;
    tableWidth?: string;
    requireParams?: boolean;
    params?: Params[];
    showFilter?: boolean;
    showSearch?: boolean;
    showCreate?: boolean;
    pageSize?: number;
    page?: number;
    ignoreZero?: boolean;
  }>(),
  {
    showQuickView: false,
    allowDisable: true,
    showHeader: true,
    showPagination: true,
    loadData: true,
    remote: true,
    rows: [],
    showActions: true,
    showShareActions: false,
    showFilter: false,
    showSearch: true,
    showCreate: true,
    pageSize: 25,
    page: 0,
  }
);

const emit = defineEmits<{
  (e: "action", action: string, item: any): void;
  (e: "rowDblClick", item: any): void;
  (e: "create"): void;
  (e: "totalRecords", totalRecords: number): void;
  (e: "lists", data: any): void;
  (e: "dataLoaded", data: any[]): void;
  (e: "search", query: string): void;
}>();

const tableActions = ref<TableAction[]>([]);

const showFilterDialog = ref(false);
const selectedRecord = ref();
const data = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const per_page = ref(25);
const sort_field = ref("");
const search_query = ref("");
const sortConfig = ref<SortConfig | null>(null);
const filteredFields = ref<FilterField[]>([]);

const handleReport = (query: string | number) => {
  console.log("datatable report = " + query);
};

const handleAction = (action: string, item: any) => {
  const file = (props.title ?? "") + " " + item.document_no
  console.log("file = " + file);
  switch (action) {
    case "toggleStatus":
      selectedRecord.value = item;
      handleAskforStatusChange(item);
      break;
    case "mail":
      if (props.endPoint) {
        documentActions(props.endPoint, item.uid, action, file);
      }
      break;
    case "share":
      if (props.endPoint) {
        documentActions(props.endPoint, item.uid, action, file);
      }
      break;
    case "download":
      if (props.endPoint) {
        documentActions(props.endPoint, item.uid, action, file);
      }
      break;
    case "print":
      if (props.endPoint) {
        documentActions(props.endPoint, item.uid, action, file);
      }
      break;
    default:
      emit("action", action, item);
      break;
  }
};

const handleAskforStatusChange = async (_item: any) => {
  const confirmed = await MessageBox.confirm({
    title: "Change Record Status",
    message: "Are you sure you want to change the Record Status?",
    confirmText: "Yes",
    cancelText: "No",
    type: "info",
  });
  if (confirmed) {
    handleConfirmStatusChange();
  }
};
const handleConfirmStatusChange = async () => {
  const apiData: { [key: string]: any } = {
    id: selectedRecord.value.uid,
    active: selectedRecord.value.active == 1 ? 0 : 1,
    page: currentPage.value,
    per_page: per_page.value,
    search_query: search_query.value,
    sort_order:
      sortConfig.value != null && sortConfig.value?.field != ""
        ? sortConfig.value?.field + "|" + sortConfig.value?.direction
        : null,
    filters: filteredFields.value,
  };
  const response = await api.post(props.endPoint + "status", apiData);
  if (response.success == true) {
    data.value = response.data.data;
    emit('dataLoaded', response.data.data);
    await setTableData(response.data);
  }
};

const setTableData = (data: any) => {
  currentPage.value = data.current_page;
  emit("totalRecords", data.total);
  totalPages.value = Math.ceil(data.total / data.per_page);
};

const handleDataLoad = async (
  showLoader: boolean = true,
  getLists: boolean = false
) => {
  if (!props.remote) {
    data.value = props.rows;
    return;
  }
  if (!props.endPoint && !props.customUrl) {
    return;
  }
  if (props.requireParams && (props.params?.length ?? 0) <= 0) {
    return;
  }
  if (!props.loadData) return;
  const apiData: { [key: string]: any } = {
    page: currentPage.value,
    per_page: per_page.value,
    search_query: search_query.value,
    sort_order:
      sortConfig.value != null && sortConfig.value?.field != ""
        ? sortConfig.value?.field + "|" + sortConfig.value?.direction
        : null,
    filters: filteredFields.value,
    get_list: getLists,
    uid: props.uid,
  };
  let validParams = true;
  props.params?.forEach((p) => {
    apiData[p.field] = p.value;
    if (p.required && (p.value == null || p.value == "")) {
      validParams = false;
    }
  });
  if (!validParams) {
    return;
  }

  let url = props.customUrl ?? "";
  if (props.endPoint && !props.customUrl) {
    url = props.endPoint;
    if (url.slice(-1) != "/") {
      url += "/";
    }
    url += "get";
  }
  const response = await api.post(url, apiData, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    skipLoading: !showLoader,
  });
  if (response.success == true) {
    data.value = response.data.data;
    emit('dataLoaded', response.data.data);
    if (response.lists) {
      emit("lists", response.lists);
      const lists = response.lists;
      for (const [name, list] of Object.entries(lists)) {
        tokenManager.saveList(name, list);
      }
      reports.value = response.lists.reports;
    }
    await setTableData(response.data);
  } else {
    data.value = [];
  }
};

const reports = ref([]);

const handleFilterApply = (filters: FilterField[]) => {
  filteredFields.value = [];
  filters.forEach((filter) => {
    console.log(filter.value);
    if (!isEmpty(filter.value)) {
      const val: FilterField = {
        name: filter.name,
        value: filter.value,
        label: "",
        type: "number",
      };
      filteredFields.value.push(val);
    }
  });
  showFilterDialog.value = false;
  currentPage.value = 1;
  handleDataLoad();
};

const handleSort = (sort: string) => {
  sortConfig.value = {
    field: sort,
    direction:
      sort == sort_field.value && sortConfig.value?.direction == "asc"
        ? "desc"
        : "asc",
  };
  sort_field.value = sort;
  currentPage.value = 1;
  handleDataLoad();
};

const handlePageChange = (page: number) => {
  currentPage.value = page;
  handleDataLoad();
};

const handleSearch = (query: string) => {
  search_query.value = query;
  currentPage.value = 1;
  handleDataLoad();
  emit('search', query);
};

tableActions.value = [
  /*{
    name: "showQuickView",
    permission: "view",
    icon: "ScanEye",
    variant: "primary",
    title: "Quick View",
    visible: () => props.showQuickView,
    ignorePermission: true,
    hideMobile: true,
  },*/
  {
    name: "view",
    permission: "create",
    icon: "Eye",
    variant: "primary",
    title: "View details",
    visible: () => true,
  },
  {
    name: "edit",
    permission: "edit",
    icon: "Pencil",
    variant: "warning",
    title: "Edit",
    visible: () => props.allowEdit,
  },
  {
    name: "download",
    permission: "create",
    icon: "Download",
    variant: "primary",
    title: "Downlaod",
    dropdown: true,
    visible: () => props.showShareActions,
    ignorePermission: true,
  },
  {
    name: "share",
    permission: "create",
    icon: "Share2",
    variant: "primary",
    title: "Share",
    dropdown: true,
    visible: () => props.showShareActions,
    ignorePermission: true,
  },
  {
    name: "mail",
    permission: "create",
    icon: "Mails",
    variant: "primary",
    title: "Email",
    dropdown: true,
    visible: () => props.showShareActions,
    ignorePermission: true,
  },
  {
    name: "print",
    permission: "create",
    icon: "Printer",
    variant: "primary",
    title: "Print",
    dropdown: true,
    visible: () => props.showShareActions,
    ignorePermission: true,
  },
];

onMounted(async () => {
  per_page.value = props.pageSize;
  nextTick(() => {});
  (props.actions ?? []).forEach((action) => {
    tableActions.value.push(action);
  });
  currentPage.value = 1;
  if (props.rows.length > 0) {
    data.value = props.rows;
  } else if (props.loadData) {
    handleDataLoad(true, true);
  }
});

watch(
  () => props.rows,
  (_newValue) => {
    data.value = props.rows;
  },
  { immediate: true }
);

watch(
  () => props.refreshData,
  async () => {
    handleDataLoad(false);
  }
);

watch(
  () => props.pageSize,
  async () => {
    per_page.value = props.pageSize;
    handleDataLoad(false);
  }
);
watch(
  () => props.page,
  (_newValue) => {
     currentPage.value = props.page;
     handleDataLoad(false);
  },
  { immediate: true }
);

</script>

<template>
  <div class="space-y-4">
    <DataTableHeader
      @search="handleSearch"
      v-if="showHeader"
      @create="$emit('create')"
      @filter="showFilterDialog = true"
      :filteredFields="filteredFields"
      :permissions="permissions"
      :reports="reports"
      :title="title"
      :show-create="showCreate"
      :show-search="showSearch"
      :show-filter="showFilter"
      @report="handleReport"
    >
      <template #table-table-header-actions>
        <slot name="list-table-header-actions"></slot>
      </template>
    </DataTableHeader>

    <div class="relative overflow-x-auto">
      <DataTableDesktop
        :items="data"
        :fields="fields"
        :actions="tableActions"
        :show-actions="showActions"
        :sort-config="sortConfig"
        :ignoreZero="ignoreZero"
        :permissions="permissions"
        :allow-disable="allowDisable"
        :show-quick-view="showQuickView"
        :show-child-row="showChildRow"
        :child-row-fields="childRowFields"
        :child-row-title="childRowTitle"
        :child-row-table-width="childRowTableWidth"
        :child-row-data-field="childRowDataField"
        :table-width="tableWidth"
        @row-dbl-click="$emit('rowDblClick', $event)"
        @sort="handleSort"
        @action="handleAction"
      />

      <DataTableMobile
        :items="data"
        :show-actions="showActions"
        :allow-disable="allowDisable"
        :show-quick-view="showQuickView"
        :titleField="titleField"
        :permissions="permissions"
        :fields="fields"
        :actions="tableActions"
        @action="handleAction"
      />
    </div>

    <DataTablePagination
      :current-page="currentPage"
      v-if="showPagination"
      :total-pages="totalPages"
      @page-change="handlePageChange"
    />

    <FilterDialog
      v-if="showFilterDialog"
      :fields="filterFields"
      :filtered-fields="filteredFields"
      @apply="handleFilterApply"
      @close="showFilterDialog = false"
    />
  </div>
</template>
